import type { FormViewT, PageViewT } from "@patient-watch/types";

import ProgressBar from "@/components/common/ProgressBar";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import { getFormPageCopy } from "@/features/i18n/formPageCopy";
import { formPagePosition, formPageProgressPercent } from "../utils/formPageProgress";

interface Props {
    form: FormViewT | null | undefined;
    pageIndex: number;
    pageTotalOverride?: number;
}

/**
 * Page progress for ePROs. Shown on every page after the instructions screen.
 *
 * The bar follows pages, including section pages that have no question.
 * Hidden when the form has only one page. Stays short of full until submit.
 */
const FormProgress = ({ form, pageIndex, pageTotalOverride }: Props) => {
    const { locale } = useAppLocale();
    const pages = (form?.pages ?? []) as PageViewT[];
    const pageTotal = pageTotalOverride ?? pages.length;

    if (pageTotal <= 1) {
        return null;
    }

    const position =
        pageTotalOverride != null
            ? pageIndex
            : formPagePosition(
                  pages.map((page) => page.page_index),
                  pageIndex
              );
    const percent = formPageProgressPercent(position, pageTotal);
    const ariaLabel = getFormPageCopy(locale).pageOf(position, pageTotal);

    return (
        <div className="flex flex-col gap-1.5 my-2 w-full text-left">
            <ProgressBar value={percent} aria-label={ariaLabel} />
        </div>
    );
};

export default FormProgress;
